import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '../lib/api/client'
import { verifyEmailProblemCopy } from '../lib/auth/problem-messages'

type VerificationState =
  | { readonly status: 'missing-token' }
  | { readonly status: 'verifying' }
  | { readonly status: 'verified' }
  | { readonly status: 'error'; readonly title: string; readonly detail: string }

/**
 * Consumes the single-use email-verification token from the link the BFF
 * emails on registration (F0.3, `POST /auth/verify-email`, C9/C19). Unknown,
 * expired, and already-used tokens are deliberately indistinguishable per the
 * contract — this screen never tries to tell them apart.
 */
export function EmailVerificationPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [state, setState] = useState<VerificationState>(
    token ? { status: 'verifying' } : { status: 'missing-token' },
  )

  const headingRef = useRef<HTMLHeadingElement | null>(null)

  // Runs once per mount for the token present at load — a verification link
  // is single-use, so there is no scenario where re-running on a later
  // re-render (vs. a fresh navigation to a new link) would be correct.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    if (!token) {
      return
    }
    const verificationToken = token
    let cancelled = false
    async function run() {
      try {
        const { data, error } = await apiClient.POST('/auth/verify-email', {
          body: { token: verificationToken },
        })
        if (cancelled) {
          return
        }
        if (data?.verified) {
          setState({ status: 'verified' })
          return
        }
        const copy = verifyEmailProblemCopy(error?.type, error?.detail)
        setState({ status: 'error', title: copy.title, detail: copy.detail })
      } catch {
        if (!cancelled) {
          setState({
            status: 'error',
            title: 'Something went wrong',
            detail: 'Please try again.',
          })
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (state.status !== 'verifying') {
      headingRef.current?.focus()
    }
  }, [state.status])

  if (state.status === 'missing-token') {
    return (
      <section className="auth-page">
        <h1 className="display-heading" ref={headingRef} tabIndex={-1}>
          Missing verification link
        </h1>
        <p>
          This page needs the verification link from your email — please use that link directly.
        </p>
        <p>
          <Link to="/signup">Create an account</Link>
        </p>
      </section>
    )
  }

  if (state.status === 'verifying') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Email verification</h1>
        <p aria-live="polite">Verifying your email&hellip;</p>
      </section>
    )
  }

  if (state.status === 'verified') {
    return (
      <section className="auth-page">
        <h1 className="display-heading" ref={headingRef} tabIndex={-1}>
          Email verified
        </h1>
        <p>Your email address has been verified. You can now sign in.</p>
        <p>
          <Link to="/login">Continue to sign in</Link>
        </p>
      </section>
    )
  }

  return (
    <section className="auth-page">
      <h1 className="display-heading" ref={headingRef} tabIndex={-1}>
        {state.title}
      </h1>
      <p role="alert">{state.detail}</p>
      <p>
        <Link to="/signup">Create an account</Link> &middot; <Link to="/login">Sign in</Link>
      </p>
    </section>
  )
}
